import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ImageUpload from "./listing-form/ImageUpload";
import BasicDetailsForm from "./listing-form/BasicDetailsForm";
import BreedingValuesForm from "./listing-form/BreedingValuesForm";
import SubmitSection from "./listing-form/SubmitSection";
import { FormData, ListingFormProps } from "./listing-form/types";

const ListingForm: React.FC<ListingFormProps> = ({
  mode,
  listingId,
  title,
  announcementId,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(mode === "edit");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [optimizedImageUrl, setOptimizedImageUrl] = useState<string>("");
  const [locationCoordinates, setLocationCoordinates] = useState<
    [number, number] | null
  >(null);

  const [formData, setFormData] = useState<FormData>({
    title: "",
    description: "",
    price: "",
    price_currency: "USD",
    breed: "",
    type: "",
    age: "",
    weight: "",
    weight_unit: "lbs",
    location: "",
    registration_number: "",
    evaluation_type: "EPD",
    calving_ease: "",
    birth_weight: "",
    birth_weight_unit: "kg",
    weaning_weight: "",
    weaning_weight_unit: "kg",
    yearling_weight: "",
    yearling_weight_unit: "kg",
    milk: "",
    milk_unit: "kg",
    total_maternal: "",
    total_maternal_unit: "kg",
    carcass_weight: "",
    carcass_weight_unit: "kg",
    marbling: "",
    intramuscular_fat: "",
    fat_thickness: "",
    fat_thickness_unit: "mm",
    maternal_weaning_weight: "",
    maternal_weaning_weight_unit: "kg",
    ribeye_area: "",
    ribeye_area_unit: "sq cm",
    gestation_length: "",
    scrotal_size: "",
    scrotal_size_unit: "cm",
    day_200_weight: "",
    day_200_weight_unit: "kg",
    day_400_weight: "",
    day_400_weight_unit: "kg",
    day_600_weight: "",
    day_600_weight_unit: "kg",
    eye_muscle_area: "",
    eye_muscle_area_unit: "sq cm",
    rump_fat: "",
    rump_fat_unit: "mm",
    retail_beef_yield: "",
    listing_type: "",
    youtube_video_url: "",
    // ACC fields
    calving_ease_acc: "",
    birth_weight_acc: "",
    weaning_weight_acc: "",
    yearling_weight_acc: "",
    milk_acc: "",
    total_maternal_acc: "",
    carcass_weight_acc: "",
    marbling_acc: "",
    intramuscular_fat_acc: "",
    fat_thickness_acc: "",
    maternal_weaning_weight_acc: "",
    ribeye_area_acc: "",
    gestation_length_acc: "",
    scrotal_size_acc: "",
    day_200_weight_acc: "",
    day_400_weight_acc: "",
    day_600_weight_acc: "",
    eye_muscle_area_acc: "",
    rump_fat_acc: "",
    retail_beef_yield_acc: "",
    // Percentile Rank fields
    calving_ease_perc_rank: "",
    birth_weight_perc_rank: "",
    weaning_weight_perc_rank: "",
    yearling_weight_perc_rank: "",
    milk_perc_rank: "",
    total_maternal_perc_rank: "",
    carcass_weight_perc_rank: "",
    marbling_perc_rank: "",
    intramuscular_fat_perc_rank: "",
    fat_thickness_perc_rank: "",
    maternal_weaning_weight_perc_rank: "",
    ribeye_area_perc_rank: "",
    gestation_length_perc_rank: "",
    scrotal_size_perc_rank: "",
    day_200_weight_perc_rank: "",
    day_400_weight_perc_rank: "",
    day_600_weight_perc_rank: "",
    eye_muscle_area_perc_rank: "",
    rump_fat_perc_rank: "",
    retail_beef_yield_perc_rank: "",
  });

  useEffect(() => {
    if (mode === "edit" && listingId && user) {
      fetchListing();
    } else {
      setFetchLoading(false);
    }
  }, [mode, listingId, user]);

  const fetchListing = async () => {
    if (!listingId) return;

    try {
      const { data, error } = await supabase
        .from("listings")
        .select("*")
        .eq("id", listingId)
        .eq("breeder_id", user?.id)
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        setFormData({
          title: data.title || "",
          description: data.description || "",
          price: data.price
            ? data.price.toString().replace(/^0+/, "") || "0"
            : "",
          price_currency: data.price_currency || "USD",
          breed: data.breed || "",
          type: data.type || "",
          age: data.age || "",
          weight: data.weight || "",
          weight_unit: data.weight_unit || "lbs",
          location: data.location || "",
          registration_number: data.registration_number || "",
          evaluation_type: data.evaluation_type || "EPD",
          calving_ease: data.calving_ease || "",
          birth_weight: data.birth_weight || "",
          birth_weight_unit: data.birth_weight_unit || "kg",
          weaning_weight: data.weaning_weight || "",
          weaning_weight_unit: data.weaning_weight_unit || "kg",
          yearling_weight: data.yearling_weight || "",
          yearling_weight_unit: data.yearling_weight_unit || "kg",
          milk: data.milk || "",
          milk_unit: data.milk_unit || "kg",
          total_maternal: data.total_maternal || "",
          total_maternal_unit: data.total_maternal_unit || "kg",
          carcass_weight: data.carcass_weight || "",
          carcass_weight_unit: data.carcass_weight_unit || "kg",
          marbling: data.marbling || "",
          intramuscular_fat: data.intramuscular_fat || "",
          fat_thickness: data.fat_thickness || "",
          fat_thickness_unit: data.fat_thickness_unit || "mm",
          maternal_weaning_weight: data.maternal_weaning_weight || "",
          maternal_weaning_weight_unit: data.maternal_weaning_weight_unit || "kg",
          ribeye_area: data.ribeye_area || "",
          ribeye_area_unit: data.ribeye_area_unit || "sq cm",
          gestation_length: data.gestation_length || "",
          scrotal_size: data.scrotal_size || "",
          scrotal_size_unit: data.scrotal_size_unit || "cm",
          day_200_weight: data.day_200_weight || "",
          day_200_weight_unit: data.day_200_weight_unit || "kg",
          day_400_weight: data.day_400_weight || "",
          day_400_weight_unit: data.day_400_weight_unit || "kg",
          day_600_weight: data.day_600_weight || "",
          day_600_weight_unit: data.day_600_weight_unit || "kg",
          eye_muscle_area: data.eye_muscle_area || "",
          eye_muscle_area_unit: data.eye_muscle_area_unit || "sq cm",
          rump_fat: data.rump_fat || "",
          rump_fat_unit: data.rump_fat_unit || "mm",
          retail_beef_yield: data.retail_beef_yield || "",
          listing_type: (data as any).listing_type || "",
          youtube_video_url: (data as any).youtube_video_url || "",
          // ACC fields
          calving_ease_acc: (data as any).calving_ease_acc || "",
          birth_weight_acc: (data as any).birth_weight_acc || "",
          weaning_weight_acc: (data as any).weaning_weight_acc || "",
          yearling_weight_acc: (data as any).yearling_weight_acc || "",
          milk_acc: (data as any).milk_acc || "",
          total_maternal_acc: (data as any).total_maternal_acc || "",
          carcass_weight_acc: (data as any).carcass_weight_acc || "",
          marbling_acc: (data as any).marbling_acc || "",
          intramuscular_fat_acc: (data as any).intramuscular_fat_acc || "",
          fat_thickness_acc: (data as any).fat_thickness_acc || "",
          maternal_weaning_weight_acc: (data as any).maternal_weaning_weight_acc || "",
          ribeye_area_acc: (data as any).ribeye_area_acc || "",
          gestation_length_acc: (data as any).gestation_length_acc || "",
          scrotal_size_acc: (data as any).scrotal_size_acc || "",
          day_200_weight_acc: (data as any).day_200_weight_acc || "",
          day_400_weight_acc: (data as any).day_400_weight_acc || "",
          day_600_weight_acc: (data as any).day_600_weight_acc || "",
          eye_muscle_area_acc: (data as any).eye_muscle_area_acc || "",
          rump_fat_acc: (data as any).rump_fat_acc || "",
          retail_beef_yield_acc: (data as any).retail_beef_yield_acc || "",
          // Percentile Rank fields
          calving_ease_perc_rank: (data as any).calving_ease_perc_rank || "",
          birth_weight_perc_rank: (data as any).birth_weight_perc_rank || "",
          weaning_weight_perc_rank: (data as any).weaning_weight_perc_rank || "",
          yearling_weight_perc_rank: (data as any).yearling_weight_perc_rank || "",
          milk_perc_rank: (data as any).milk_perc_rank || "",
          total_maternal_perc_rank: (data as any).total_maternal_perc_rank || "",
          carcass_weight_perc_rank: (data as any).carcass_weight_perc_rank || "",
          marbling_perc_rank: (data as any).marbling_perc_rank || "",
          intramuscular_fat_perc_rank: (data as any).intramuscular_fat_perc_rank || "",
          fat_thickness_perc_rank: (data as any).fat_thickness_perc_rank || "",
          maternal_weaning_weight_perc_rank: (data as any).maternal_weaning_weight_perc_rank || "",
          ribeye_area_perc_rank: (data as any).ribeye_area_perc_rank || "",
          gestation_length_perc_rank: (data as any).gestation_length_perc_rank || "",
          scrotal_size_perc_rank: (data as any).scrotal_size_perc_rank || "",
          day_200_weight_perc_rank: (data as any).day_200_weight_perc_rank || "",
          day_400_weight_perc_rank: (data as any).day_400_weight_perc_rank || "",
          day_600_weight_perc_rank: (data as any).day_600_weight_perc_rank || "",
          eye_muscle_area_perc_rank: (data as any).eye_muscle_area_perc_rank || "",
          rump_fat_perc_rank: (data as any).rump_fat_perc_rank || "",
          retail_beef_yield_perc_rank: (data as any).retail_beef_yield_perc_rank || "",
        });

        if (data.location_coordinates) {
          try {
            let coordinates;
            if (typeof data.location_coordinates === "string") {
              coordinates = JSON.parse(data.location_coordinates);
            } else {
              coordinates = data.location_coordinates;
            }

            if (
              coordinates &&
              typeof coordinates === "object" &&
              "longitude" in coordinates &&
              "latitude" in coordinates &&
              typeof coordinates.longitude === "number" &&
              typeof coordinates.latitude === "number"
            ) {
              setLocationCoordinates([
                coordinates.longitude,
                coordinates.latitude,
              ]);
            }
          } catch (error) {
            console.log("Error parsing location coordinates:", error);
          }
        }

        let imageArray: string[] = [];
        if (data.images) {
          if (Array.isArray(data.images)) {
            imageArray = data.images
              .map((img) => {
                if (typeof img === "string") {
                  return img;
                }
                return String(img);
              })
              .filter((img) => img && img !== "null");
          } else if (typeof data.images === "string") {
            try {
              const parsed = JSON.parse(data.images);
              if (Array.isArray(parsed)) {
                imageArray = parsed
                  .map((img) => String(img))
                  .filter((img) => img && img !== "null");
              }
            } catch {
              imageArray = [];
            }
          }
        }
        setExistingImages(imageArray);
        if (imageArray.length > 0) {
          setImagePreview(imageArray[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast({
        title: "Error",
        description: "Failed to load listing. Please try again.",
        variant: "destructive",
      });
      navigate("/breeder-dashboard");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    const numericFieldPrefixes = [
      'calving_ease', 'birth_weight', 'weaning_weight', 'yearling_weight',
      'milk', 'total_maternal', 'carcass_weight', 'marbling',
      'intramuscular_fat', 'fat_thickness', 'maternal_weaning_weight',
      'ribeye_area', 'gestation_length', 'scrotal_size',
      'day_200_weight', 'day_400_weight', 'day_600_weight',
      'eye_muscle_area', 'rump_fat', 'retail_beef_yield'
    ];
    const isNumericField = field === 'price' || numericFieldPrefixes.some(
      p => field === p || field === `${p}_acc` || field === `${p}_perc_rank`
    );
    if (isNumericField && value) {
      value = value.replace(/^0+(?=\d)/, '');
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (
    location: string,
    coordinates?: [number, number]
  ) => {
    handleInputChange("location", location);
    setLocationCoordinates(coordinates || null);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOptimizedImageUrl = (url: string) => {
    setOptimizedImageUrl(url);
  };

  const isValidYoutubeUrl = (url: string): boolean => {
    if (!url.trim()) return true; // empty is valid (optional field)
    return /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[a-zA-Z0-9_-]+/.test(url.trim());
  };

  const handleSave = async () => {
    if (!user) return;

    // Validate YouTube URL
    if (formData.youtube_video_url && !isValidYoutubeUrl(formData.youtube_video_url)) {
      toast({
        title: "Invalid YouTube URL",
        description: "Please enter a valid YouTube URL (youtube.com/watch, youtu.be, or youtube.com/embed).",
        variant: "destructive",
      });
      return;
    }

    // Validate breeding values are within limits
    const breedingValueFields = [
      'calving_ease', 'birth_weight', 'weaning_weight', 'yearling_weight', 'milk',
      'total_maternal', 'carcass_weight', 'marbling', 'intramuscular_fat', 'fat_thickness',
      'maternal_weaning_weight', 'ribeye_area', 'gestation_length', 'scrotal_size',
      'day_200_weight', 'day_400_weight', 'day_600_weight', 'eye_muscle_area',
      'rump_fat', 'retail_beef_yield'
    ];

    for (const field of breedingValueFields) {
      const value = formData[field as keyof typeof formData];
      if (value && parseFloat(value) > 100000) {
        toast({
          title: "Invalid Value",
          description: `Breeding values cannot exceed 100,000. Please check ${field.replace(/_/g, ' ')}.`,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      let imageUrls = mode === "edit" ? existingImages : [];

      // Use optimized image URL if available
      if (optimizedImageUrl) {
        imageUrls = [optimizedImageUrl];
      } else if (mode === "add" && imageFile) {
        // Fallback: upload raw file directly if background compression hasn't finished
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("Please log in to upload images");
        const fileName = `${session.user.id}/${Date.now()}.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('listing-images')
          .upload(fileName, imageFile, { contentType: imageFile.type });
        if (uploadError) throw uploadError;
        const { data: { publicUrl } } = supabase.storage
          .from('listing-images')
          .getPublicUrl(fileName);
        imageUrls = [publicUrl];
      } else if (mode === "add" && !imageFile) {
        throw new Error("Please select an image for your listing");
      }

      // Determine the status based on whether this is linked to an announcement
      const listingStatus = announcementId ? "upcoming" : "active";

      const listingData = {
        title: formData.title,
        description: formData.description,
        price: formData.price ? parseFloat(formData.price) : null,
        price_currency: formData.price_currency,
        breed: formData.breed,
        type: formData.type,
        age: formData.age || null,
        weight: formData.weight || null,
        weight_unit: formData.weight_unit,
        location: formData.location,
        location_coordinates: locationCoordinates
          ? JSON.stringify({
              longitude: locationCoordinates[0],
              latitude: locationCoordinates[1],
            })
          : null,
        images: imageUrls,
        registration_number: formData.registration_number || null,
        evaluation_type: formData.evaluation_type,
        calving_ease: formData.calving_ease || null,
        birth_weight: formData.birth_weight || null,
        birth_weight_unit: formData.birth_weight_unit,
        weaning_weight: formData.weaning_weight || null,
        weaning_weight_unit: formData.weaning_weight_unit,
        yearling_weight: formData.yearling_weight || null,
        yearling_weight_unit: formData.yearling_weight_unit,
        milk: formData.milk || null,
        milk_unit: formData.milk_unit,
        total_maternal: formData.total_maternal || null,
        total_maternal_unit: formData.total_maternal_unit,
        carcass_weight: formData.carcass_weight || null,
        carcass_weight_unit: formData.carcass_weight_unit,
        marbling: formData.marbling || null,
        intramuscular_fat: formData.intramuscular_fat || null,
        fat_thickness: formData.fat_thickness || null,
        fat_thickness_unit: formData.fat_thickness_unit,
        maternal_weaning_weight: formData.maternal_weaning_weight || null,
        maternal_weaning_weight_unit: formData.maternal_weaning_weight_unit,
        ribeye_area: formData.ribeye_area || null,
        ribeye_area_unit: formData.ribeye_area_unit,
        gestation_length: formData.gestation_length || null,
        scrotal_size: formData.scrotal_size || null,
        scrotal_size_unit: formData.scrotal_size_unit,
        day_200_weight: formData.day_200_weight || null,
        day_200_weight_unit: formData.day_200_weight_unit,
        day_400_weight: formData.day_400_weight || null,
        day_400_weight_unit: formData.day_400_weight_unit,
        day_600_weight: formData.day_600_weight || null,
        day_600_weight_unit: formData.day_600_weight_unit,
        eye_muscle_area: formData.eye_muscle_area || null,
        eye_muscle_area_unit: formData.eye_muscle_area_unit,
        rump_fat: formData.rump_fat || null,
        rump_fat_unit: formData.rump_fat_unit,
        retail_beef_yield: formData.retail_beef_yield || null,
        listing_type: formData.listing_type || null,
        youtube_video_url: formData.youtube_video_url.trim() ? formData.youtube_video_url.trim() : null,
        // ACC fields
        calving_ease_acc: formData.calving_ease_acc || null,
        birth_weight_acc: formData.birth_weight_acc || null,
        weaning_weight_acc: formData.weaning_weight_acc || null,
        yearling_weight_acc: formData.yearling_weight_acc || null,
        milk_acc: formData.milk_acc || null,
        total_maternal_acc: formData.total_maternal_acc || null,
        carcass_weight_acc: formData.carcass_weight_acc || null,
        marbling_acc: formData.marbling_acc || null,
        intramuscular_fat_acc: formData.intramuscular_fat_acc || null,
        fat_thickness_acc: formData.fat_thickness_acc || null,
        maternal_weaning_weight_acc: formData.maternal_weaning_weight_acc || null,
        ribeye_area_acc: formData.ribeye_area_acc || null,
        gestation_length_acc: formData.gestation_length_acc || null,
        scrotal_size_acc: formData.scrotal_size_acc || null,
        day_200_weight_acc: formData.day_200_weight_acc || null,
        day_400_weight_acc: formData.day_400_weight_acc || null,
        day_600_weight_acc: formData.day_600_weight_acc || null,
        eye_muscle_area_acc: formData.eye_muscle_area_acc || null,
        rump_fat_acc: formData.rump_fat_acc || null,
        retail_beef_yield_acc: formData.retail_beef_yield_acc || null,
        // Percentile Rank fields
        calving_ease_perc_rank: formData.calving_ease_perc_rank || null,
        birth_weight_perc_rank: formData.birth_weight_perc_rank || null,
        weaning_weight_perc_rank: formData.weaning_weight_perc_rank || null,
        yearling_weight_perc_rank: formData.yearling_weight_perc_rank || null,
        milk_perc_rank: formData.milk_perc_rank || null,
        total_maternal_perc_rank: formData.total_maternal_perc_rank || null,
        carcass_weight_perc_rank: formData.carcass_weight_perc_rank || null,
        marbling_perc_rank: formData.marbling_perc_rank || null,
        intramuscular_fat_perc_rank: formData.intramuscular_fat_perc_rank || null,
        fat_thickness_perc_rank: formData.fat_thickness_perc_rank || null,
        maternal_weaning_weight_perc_rank: formData.maternal_weaning_weight_perc_rank || null,
        ribeye_area_perc_rank: formData.ribeye_area_perc_rank || null,
        gestation_length_perc_rank: formData.gestation_length_perc_rank || null,
        scrotal_size_perc_rank: formData.scrotal_size_perc_rank || null,
        day_200_weight_perc_rank: formData.day_200_weight_perc_rank || null,
        day_400_weight_perc_rank: formData.day_400_weight_perc_rank || null,
        day_600_weight_perc_rank: formData.day_600_weight_perc_rank || null,
        eye_muscle_area_perc_rank: formData.eye_muscle_area_perc_rank || null,
        rump_fat_perc_rank: formData.rump_fat_perc_rank || null,
        retail_beef_yield_perc_rank: formData.retail_beef_yield_perc_rank || null,
      } as any;

      if (mode === "add") {
        const { error } = await supabase.from("listings").insert({
          breeder_id: user.id,
          ...listingData,
          status: listingStatus,
          announcement_id: announcementId || null,
        });

        if (error) {
          throw error;
        }
      } else {
        const { error } = await supabase
          .from("listings")
          .update({
            ...listingData,
            updated_at: new Date().toISOString(),
          })
          .eq("id", listingId)
          .eq("breeder_id", user.id);

        if (error) {
          throw error;
        }
      }

      const successMessage = announcementId
        ? `Your listing has been ${
            mode === "add" ? "added to the announcement" : "updated"
          } successfully.`
        : `Your listing has been ${
            mode === "add" ? "saved" : "updated"
          } successfully.`;

      toast({
        title: mode === "add" ? "Listing Added" : "Listing Updated",
        description: successMessage,
      });

      navigate("/breeder-dashboard");
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: `${mode === "add" ? "Save" : "Update"} Failed`,
        description: `There was an error ${
          mode === "add" ? "saving" : "updating"
        } your listing. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
      </div>
    );
  }

  const hasImage =
    mode === "edit"
      ? existingImages.length > 0 ||
        Boolean(imageFile) ||
        Boolean(optimizedImageUrl)
      : Boolean(imageFile) || Boolean(optimizedImageUrl);
  const isFormValid =
    formData.title && formData.breed && formData.type && hasImage;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate("/breeder-dashboard")}
        className="inline-flex items-center text-green-700 hover:text-green-800 mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Dashboard
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-8">{title}</h1>

      {announcementId && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-700">
            This listing will be attached to your announcement and will not be
            visible in the marketplace.
          </p>
        </div>
      )}

      <ImageUpload
        imagePreview={imagePreview}
        imageFile={imageFile}
        existingImages={existingImages}
        mode={mode}
        onImageChange={handleImageChange}
        onOptimizedImageUrl={handleOptimizedImageUrl}
      />

      <BasicDetailsForm
        formData={formData}
        onInputChange={handleInputChange}
        onLocationChange={handleLocationChange}
      />

      <BreedingValuesForm
        formData={formData}
        onInputChange={handleInputChange}
      />

      <SubmitSection
        mode={mode}
        isFormValid={isFormValid}
        loading={loading}
        onSubmit={handleSave}
      />
    </div>
  );
};

export default ListingForm;
